import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileSignature, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { getAgreement, signAgreement, formatApiErrorDetail } from "@/api";

const AGREEMENT_SECTIONS = [
  {
    title: "Parties and purpose",
    body: "This agreement is between Comprehensive Chain Management (Pty) Ltd t/a CompCar Motors (\"CompCar\") and you, the applicant named above. It records a rent-to-own arrangement: you take possession and private use of the vehicle while scheduled payments build your ownership balance, and ownership transfers to you when that balance reaches zero.",
  },
  {
    title: "Vehicle and possession",
    body: "The vehicle identified in your written quotation is supplied roadworthy, insured and fitted with an approved tracking device. Possession and ordinary private use pass to you on collection; ownership remains with CompCar until transfer.",
  },
  {
    title: "Payments, deposits and ownership allocation",
    body: "You pay an ownership deposit and a refundable security deposit before collection, then a fixed monthly amount by debit order on your agreed day. Each monthly payment is split in writing into a rental & service charge and an ownership allocation. The ownership allocation reduces your ownership balance every month, and a monthly statement proves your progress.",
  },
  {
    title: "No hidden balloon",
    body: "The agreement runs for the defined term shown in your quotation (48 or 60 months). There is no undisclosed balloon or residual payment. Your early-settlement figure is available on request at any time.",
  },
  {
    title: "Insurance, tracking and monitoring",
    body: "The vehicle remains comprehensively insured under approved arrangements included in your rental & service charge, and you consent to lawful mileage and location monitoring via the tracker for the duration of the agreement.",
  },
  {
    title: "Maintenance and care",
    body: "Routine maintenance is covered as set out in your agreement. You must keep the vehicle in good condition, report damage or faults promptly, and not modify the vehicle without written consent.",
  },
  {
    title: "Use restrictions",
    body: "The vehicle is for private personal and ordinary commuting use only. It may not be used for e-hailing, courier, delivery, taxi or any carriage of goods or passengers for reward, may not be sub-rented, and may not leave South Africa without written consent.",
  },
  {
    title: "Ownership transfer",
    body: "When your ownership balance reaches zero — at the end of the term or on early settlement — CompCar completes transfer and the vehicle is registered in your name. The security deposit is refunded in line with the agreement.",
  },
  {
    title: "Early settlement",
    body: "You may settle the remaining ownership balance at any time and take ownership sooner. The settlement figure is the ownership balance at that date, with no hidden penalties beyond what the written agreement states.",
  },
  {
    title: "Missed payments, termination and repossession",
    body: "If a debit order fails, we will contact you to rearrange. Continued non-payment may lead to lawful termination of the agreement and repossession of the vehicle, as set out in the full written agreement and applicable law. Amounts already allocated to ownership are dealt with as the agreement provides.",
  },
  {
    title: "Information, verification and POPIA",
    body: "You confirm the information in your application is complete and true, and you consent to CompCar processing it under POPIA for identity, affordability, credit and verification purposes, and sharing only what is necessary with verification, payment, insurance and tracking partners.",
  },
  {
    title: "This online signature",
    body: "Typing your full legal name below and accepting the declaration records your agreement to proceed, together with the date, time and device network address. The complete written consumer agreement and pre-agreement statement are provided to you and prevail over this summary.",
  },
];

export default function AgreementSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => {
    getAgreement(token)
      .then((d) => {
        setData(d);
        setName(d.full_name || "");
      })
      .catch(() => setError("This agreement link is invalid or has expired."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signAgreement(token, { signed_name: name, consent });
      setJustSigned(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div data-testid="agreement-error" className="min-h-screen flex items-center justify-center px-4 pt-20 text-center">
        <div>
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-white mb-3">Link not valid</h1>
          <p className="text-slate-400 text-sm mb-8">{error}</p>
          <Link to="/contact" className="text-sky-300 hover:text-white font-bold text-sm">Contact us for help</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="agreement-loading">Loading agreement…</div>;
  }

  if (data.signed || justSigned) {
    return (
      <div data-testid="agreement-signed" className="min-h-screen flex items-center justify-center px-4 pt-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
          <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-8" />
          <h1 className="font-display text-4xl font-black text-white mb-4">Agreement signed.</h1>
          <p className="text-slate-400 text-sm leading-relaxed mb-2">
            Signed by <strong className="text-white">{data.signed_name || name}</strong>
            {data.reference && <> · Reference <strong className="text-[#F57C00]">{data.reference}</strong></>}
          </p>
          <p className="text-slate-500 text-xs leading-relaxed">
            CompCar Motors will be in touch to arrange collection and final
            paperwork. Keep your reference number handy.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="agreement-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-3xl mx-auto px-4 sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">CompCar Motors · Rent-to-own</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-4">
          Consumer agreement.
        </h1>
        <p className="text-sm text-slate-400 mb-10">
          Hi <strong className="text-white">{data.full_name}</strong> — please review the key terms of your
          rent-to-own agreement{data.reference && <> (reference <strong className="text-[#F57C00]">{data.reference}</strong>)</>}.
          The full written agreement and quotation are provided to you separately before this becomes binding.
        </p>

        {(data.vehicle?.make || data.vehicle?.model) && (
          <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-6 mb-8" data-testid="agreement-vehicle">
            <p className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1">Your vehicle</p>
            <p className="font-display text-xl font-black text-white">
              {data.vehicle.make} {data.vehicle.model}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              Term: {data.vehicle.term} months{data.vehicle.max_monthly && <> · Budgeted monthly: R {Number(data.vehicle.max_monthly).toLocaleString("en-ZA")}</>}
            </p>
          </div>
        )}

        <div className="bg-[#122659]/60 border border-sky-300/20 rounded-xl p-8 mb-10" data-testid="agreement-terms">
          <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mb-6">Full agreement terms</p>
          <div className="space-y-6">
            {AGREEMENT_SECTIONS.map((s, i) => (
              <div key={s.title} data-testid={`agreement-section-${i + 1}`}>
                <h3 className="font-display text-sm font-bold text-white mb-1.5">
                  <span className="text-[#F57C00] mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed pl-8">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} data-testid="agreement-form" className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 space-y-6">
          <div className="flex items-center gap-3">
            <FileSignature size={22} className="text-[#F57C00]" />
            <h2 className="font-display text-xl font-black text-white">Sign online</h2>
          </div>
          <div>
            <label className="input-label" htmlFor="ag-name">Type your full legal name as your signature</label>
            <input
              id="ag-name"
              data-testid="agreement-name-input"
              required
              className="input-field font-display text-lg"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              data-testid="agreement-consent-check"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#F57C00]"
            />
            <span className="text-sm text-slate-300 leading-relaxed group-hover:text-white transition-colors">
              I have read and understood the key terms above and agree to proceed with the
              rent-to-own agreement. I understand the full written agreement follows and that
              my typed name, date and time are recorded as my signature.
            </span>
          </label>
          <button
            type="submit"
            data-testid="agreement-sign-btn"
            disabled={loading || !consent}
            className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? "Signing…" : "Sign agreement"}
          </button>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Questions before signing? Call 010 534 9926 or email admin@compcarmotors.co.za —
            we're happy to walk you through every clause.
          </p>
        </form>
      </div>
    </div>
  );
}
